import { useQuery } from '@tanstack/react-query';
import { FilterState } from '../../types/filters';
import * as api from '../../services/api';
import { queryClient } from '../../lib/queryClient';


const normalizeFiltersForKey = (filters: FilterState) => {
  return {
    dateRange: filters.dateRange ? {
      startDate: filters.dateRange.startDate,
      endDate: filters.dateRange.endDate
    } : null,
    
    dayOfWeek: filters.dayOfWeek ? {
      allDays: filters.dayOfWeek.allDays,
      selectedDays: filters.dayOfWeek.allDays ? [] : [...filters.dayOfWeek.selectedDays].sort()
    } : null,
    
    time: filters.time ? {
      allHours: filters.time.allHours,
      selectedHours: filters.time.allHours ? [] : [...filters.time.selectedHours].sort()
    } : null,
    
    diningBehavior: filters.diningBehavior ? {
      allBehaviors: filters.diningBehavior.allBehaviors,
      selectedBehaviors: filters.diningBehavior.allBehaviors ? [] : [...filters.diningBehavior.selectedBehaviors].sort()
    } : null
  };
};


export const dashboardQueryKeys = {
  
  all: ['dashboard'] as const,
  
  
  store: (storeId: string) => [...dashboardQueryKeys.all, storeId] as const,
  
  
  main: (storeId: string, filters: FilterState) => [
    ...dashboardQueryKeys.store(storeId), 
    'main', 
    normalizeFiltersForKey(filters)
  ] as const,
  
  
  kpi: {
    monthly: (storeId: string, filters: Partial<FilterState>) => [
      ...dashboardQueryKeys.store(storeId),
      'kpi',
      'monthly',
      filters
    ] as const,
    
    comparison: (storeId: string, filters: Partial<FilterState>) => [
      ...dashboardQueryKeys.store(storeId),
      'kpi', 
      'comparison',
      filters
    ] as const,
  },
  
  
  charts: {
    yearly: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'charts',
      'yearly'
    ] as const,
    
    quarterly: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'charts',
      'quarterly'
    ] as const,
    
    daily: (storeId: string, yearMonth: string) => [
      ...dashboardQueryKeys.store(storeId),
      'charts',
      'daily',
      yearMonth
    ] as const,
  },

  
  diningGroup: {
    monthlyTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'dining-group',
      'monthly-trend'
    ] as const,
    
    optionsMonthlyTrend: (storeId: string, diningGroup: string) => [
      ...dashboardQueryKeys.store(storeId),
      'dining-group',
      'options-monthly-trend',
      diningGroup
    ] as const,
  },

  
  diningBehavior: {
    monthlyTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'dining-behavior',
      'monthly-trend'
    ] as const,
    
    optionsMonthlyTrend: (storeId: string, behavior: string) => [
      ...dashboardQueryKeys.store(storeId),
      'dining-behavior',
      'options-monthly-trend',
      behavior
    ] as const,
  },

  
  dailySales: {
    monthlyDayOfWeekTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'daily-sales',
      'monthly-day-of-week-trend'
    ] as const,
  },

  
  hourlySales: {
    monthlyHourlyTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'hourly-sales',
      'monthly-hourly-trend'
    ] as const,
  },

  
  menuGroup: {
    all: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'menu-group'
    ] as const,
    categories: (storeId: string, menuGroup: string) => [
      ...dashboardQueryKeys.store(storeId),
      'menu-group',
      'categories',
      menuGroup
    ] as const,
  },
  
  
  campaigns: {
    all: (storeId: string) => [...dashboardQueryKeys.store(storeId), 'campaigns'] as const,
    byDateRange: (storeId: string, startYearMonth?: string, endYearMonth?: string) => [
      ...dashboardQueryKeys.store(storeId), 
      'campaigns', 
      'dateRange',
      { startYearMonth, endYearMonth }
    ] as const,
    monthlyTrendWithCampaigns: (storeId: string, filters: Partial<FilterState>) => [
      ...dashboardQueryKeys.store(storeId),
      'campaigns',
      'monthlyTrend',
      normalizeFiltersForKey(filters as FilterState)
    ] as const,
  },

  
  menuCategory: {
    monthlyTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'menu-category',
      'monthly-trend'
    ] as const,
    
    categoriesForTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'menu-category',
      'categories-for-trend'
    ] as const,
  },

  
  menuItems: {
    monthlyData: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'menu-items',
      'monthly-data'
    ] as const,
    
    monthlyTrend: (storeId: string, menuGroup: string, menuCategory: string) => [
      ...dashboardQueryKeys.store(storeId),
      'menu-items',
      'monthly-trend',
      menuGroup,
      menuCategory
    ] as const,
  },

  
  reviews: {
    monthlyTrend: (storeId: string) => [
      ...dashboardQueryKeys.store(storeId),
      'reviews',
      'monthly-trend'
    ] as const,
  }
};


export interface UseDashboardQueryOptions {
  storeId: string;
  filters: FilterState;
  enabled?: boolean;
}

export const useDashboardQuery = ({ 
  storeId, 
  filters, 
  enabled = true 
}: UseDashboardQueryOptions) => {
  const queryKey = dashboardQueryKeys.main(storeId, filters);
  
  
  
  const queryCache = queryClient.getQueryCache();
  
  return useQuery({
    queryKey,
    queryFn: async () => {
      
      const filterParams = api.buildFilterParams(filters);
      const response = await api.getDashboardData(storeId, filterParams);
      
      return response;
    },
    enabled: enabled && !!storeId,
    
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,        
    
    
    refetchOnMount: false,              
    refetchInterval: false,             
  });
};


export interface UseKpiMonthlyQueryOptions {
  storeId: string;
  filters: Partial<FilterState>;
  enabled?: boolean;
}

export const useKpiMonthlyQuery = ({
  storeId,
  filters,
  enabled = true
}: UseKpiMonthlyQueryOptions) => {
  return useQuery({
    queryKey: dashboardQueryKeys.kpi.monthly(storeId, filters),
    queryFn: async () => {
      
      const filterParams: any = {};
      
      
      if (filters.dayOfWeek && !filters.dayOfWeek.allDays && filters.dayOfWeek.selectedDays.length > 0) {
        filterParams.dayOfWeek = filters.dayOfWeek.selectedDays;
      }
      
      if (filters.time && !filters.time.allHours && filters.time.selectedHours.length > 0) {
        filterParams.hour = filters.time.selectedHours;
      }
      
      if (filters.diningBehavior && !filters.diningBehavior.allBehaviors && filters.diningBehavior.selectedBehaviors.length > 0) {
        filterParams.behavior = filters.diningBehavior.selectedBehaviors;
      }
      
      const response = await api.getKpiMonthlyTrend(storeId, filterParams);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
  });
};


export const useKpiComparisonQuery = ({
  storeId,
  filters,
  enabled = true
}: UseKpiMonthlyQueryOptions) => {
  return useQuery({
    queryKey: dashboardQueryKeys.kpi.comparison(storeId, filters),
    queryFn: async () => {
      
      const response = await api.getMonthComparisonData(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
  });
};


export const useDailyChartWithCampaignsQuery = (
  storeId: string, 
  yearMonth: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: [...dashboardQueryKeys.charts.daily(storeId, yearMonth), 'with-campaigns'],
    queryFn: async () => {
      const response = await fetch(`/api/v1/dashboard/salesforce/${storeId}/daily-trend-with-campaigns?yearMonth=${yearMonth}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch daily trend data with campaigns: ${response.statusText}`);
      }
      return response.json();
    },
    enabled: enabled && !!storeId && !!yearMonth,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
  });
};


export const useYearlyTrendQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.charts.yearly(storeId),
    queryFn: async () => {
      
      const response = await api.getYearlyTrendData(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,         
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};


export const useQuarterlyTrendQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.charts.quarterly(storeId),
    queryFn: async () => {
      const response = await api.getQuarterlyTrendData(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,         
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};


export const useDailyChartQuery = (
  storeId: string, 
  yearMonth: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.charts.daily(storeId, yearMonth),
    queryFn: async () => {
      
      const { startDate, endDate } = getMonthDateRange(yearMonth);
      const response = await api.getDashboardData(storeId, { startDate, endDate });
      
      if (response?.chartData?.dailyTrend) {
        const sortedTrend = [...response.chartData.dailyTrend].sort((a, b) => 
          new Date(a.business_date).getTime() - new Date(b.business_date).getTime()
        );
        
        return sortedTrend;
      }
      
      return [];
    },
    enabled: enabled && !!storeId && !!yearMonth,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
  });
};


const getMonthDateRange = (yearMonth: string): { startDate: string; endDate: string } => {
  const [year, month] = yearMonth.split('-');
  const startDate = `${year}-${month}-01`;
  
  const yearNum = parseInt(year, 10);
  const monthNum = parseInt(month, 10);
  const lastDay = new Date(yearNum, monthNum, 0).getDate();
  
  const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;
  
  return { startDate, endDate };
};


export const invalidateDashboardQueries = (queryClient: any, storeId?: string) => {
  if (storeId) {
    queryClient.invalidateQueries({ 
      queryKey: dashboardQueryKeys.store(storeId) 
    });
  } else {
    queryClient.invalidateQueries({ 
      queryKey: dashboardQueryKeys.all 
    });
  }
};


export const useDiningGroupMonthlyTrendQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.diningGroup.monthlyTrend(storeId),
    queryFn: async () => {
      const response = await api.getDiningGroupMonthlyTrend(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 10 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,            
  });
};


export const useDiningOptionsMonthlyTrendQuery = (
  storeId: string, 
  diningGroup: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.diningGroup.optionsMonthlyTrend(storeId, diningGroup),
    queryFn: async () => {
      const response = await api.getDiningOptionsMonthlyTrend(storeId, diningGroup);
      return response;
    },
    enabled: enabled && !!storeId && !!diningGroup,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useDiningBehaviorMonthlyTrendQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.diningBehavior.monthlyTrend(storeId),
    queryFn: async () => {
      const response = await api.getDiningBehaviorMonthlyTrend(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 10 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useBehaviorOptionsMonthlyTrendQuery = (
  storeId: string, 
  behavior: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.diningBehavior.optionsMonthlyTrend(storeId, behavior),
    queryFn: async () => {
      const response = await api.getBehaviorOptionsMonthlyTrend(storeId, behavior);
      return response;
    },
    enabled: enabled && !!storeId && !!behavior,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMonthlyDayOfWeekTrendQuery = (
  storeId: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.dailySales.monthlyDayOfWeekTrend(storeId),
    queryFn: async () => {
      const response = await api.getMonthlyDayOfWeekTrend(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMonthlyHourlyTrendQuery = (
  storeId: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.hourlySales.monthlyHourlyTrend(storeId),
    queryFn: async () => {
      const response = await api.getMonthlyHourlyTrend(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,    
    gcTime: 24 * 60 * 60 * 1000,       
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMenuGroupQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.menuGroup.all(storeId),
    queryFn: async () => {
      const response = await api.getMenuGroupData(storeId);
      return response;
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMenuCategoryQuery = (
  storeId: string, 
  menuGroup: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.menuGroup.categories(storeId, menuGroup),
    queryFn: async () => {
      const response = await api.getMenuCategoryData(storeId, { menuGroup });
      return response;
    },
    enabled: enabled && !!storeId && !!menuGroup,
    
    staleTime: 10 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};

export const prefetchDashboard = (queryClient: any, storeId: string, filters: FilterState) => {
  return queryClient.prefetchQuery({
    queryKey: dashboardQueryKeys.main(storeId, filters),
    queryFn: async () => {
      const filterParams = api.buildFilterParams(filters);
      return api.getDashboardData(storeId, filterParams);
    },
    staleTime: 5 * 60 * 1000,
  });
};






export const useStoreCampaignsQuery = (
  storeId: string, 
  startYearMonth?: string, 
  endYearMonth?: string,
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.campaigns.byDateRange(storeId, startYearMonth, endYearMonth),
    queryFn: async () => {
      const response = await fetch(`/api/v1/dashboard/salesforce/${storeId}/campaigns?${new URLSearchParams({
        ...(startYearMonth && { startYearMonth }),
        ...(endYearMonth && { endYearMonth })
      })}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch campaigns');
      }
      
      return response.json();
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMonthlyTrendWithCampaignsQuery = (
  storeId: string, 
  filters: Partial<FilterState> = {},
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.campaigns.monthlyTrendWithCampaigns(storeId, filters),
    queryFn: async () => {
      const filterParams = new URLSearchParams();
      
      
      if (filters.dateRange) {
        filterParams.append('startDate', filters.dateRange.startDate);
        filterParams.append('endDate', filters.dateRange.endDate);
      }
      
      if (filters.dayOfWeek && !filters.dayOfWeek.allDays) {
        filterParams.append('days', filters.dayOfWeek.selectedDays.join(','));
        filterParams.append('allDays', 'false');
      }
      
      if (filters.time && !filters.time.allHours) {
        filterParams.append('hours', filters.time.selectedHours.join(','));
        filterParams.append('allHours', 'false');
      }
      
      if (filters.diningBehavior && !filters.diningBehavior.allBehaviors) {
        filterParams.append('behaviors', filters.diningBehavior.selectedBehaviors.join(','));
        filterParams.append('allBehaviors', 'false');
      }
      
      const response = await fetch(`/api/v1/dashboard/salesforce/${storeId}/monthly-trend-with-campaigns?${filterParams}`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch monthly trend with campaigns');
      }
      
      return response.json();
    },
    enabled: enabled && !!storeId,
    
    staleTime: 5 * 60 * 1000,         
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};






export const useMenuCategoryMonthlyTrendQuery = (
  storeId: string, 
  filters: Partial<FilterState> = {},
  enabled = true
) => {
  return useQuery({
    queryKey: [...dashboardQueryKeys.menuCategory.monthlyTrend(storeId), 'filtered', normalizeFiltersForKey(filters as FilterState)],
    queryFn: async () => {
      const filterParams = new URLSearchParams();
      
      
      if (filters.dayOfWeek && !filters.dayOfWeek.allDays && filters.dayOfWeek.selectedDays.length > 0) {
        filterParams.append('dayOfWeek', filters.dayOfWeek.selectedDays.join(','));
      }
      
      if (filters.time && !filters.time.allHours && filters.time.selectedHours.length > 0) {
        filterParams.append('hour', filters.time.selectedHours.join(','));
      }
      
      if (filters.diningBehavior && !filters.diningBehavior.allBehaviors && filters.diningBehavior.selectedBehaviors.length > 0) {
        filterParams.append('behavior', filters.diningBehavior.selectedBehaviors.join(','));
      }
      
      const url = `/api/v1/dashboard/salesforce/${storeId}/category-monthly-trend${filterParams.toString() ? `?${filterParams}` : ''}`;
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error('Failed to fetch menu category monthly trend');
      }
      return response.json();
    },
    enabled: enabled && !!storeId,
    
    staleTime: 10 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMenuCategoriesForTrendQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.menuCategory.categoriesForTrend(storeId),
    queryFn: async () => {
      const response = await fetch(`/api/v1/dashboard/salesforce/${storeId}/menu-categories-for-trend`);
      if (!response.ok) {
        throw new Error('Failed to fetch menu categories for trend');
      }
      return response.json();
    },
    enabled: enabled && !!storeId,
    
    staleTime: 12 * 60 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMonthlyMenuItemsDataQuery = (storeId: string, enabled = true) => {
  return useQuery({
    queryKey: dashboardQueryKeys.menuItems.monthlyData(storeId),
    queryFn: async () => {
      const response = await fetch(`/api/v1/dashboard/salesforce/${storeId}/monthly-menu-items-data`);
      if (!response.ok) {
        throw new Error('Failed to fetch monthly menu items data');
      }
      return response.json();
    },
    enabled: enabled && !!storeId,
    
    staleTime: 10 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};


export const useMenuItemsMonthlyTrendQuery = (
  storeId: string, 
  menuGroup: string, 
  menuCategory: string, 
  enabled = true
) => {
  return useQuery({
    queryKey: dashboardQueryKeys.menuItems.monthlyTrend(storeId, menuGroup, menuCategory),
    queryFn: async () => {
      const params = new URLSearchParams({
        menuGroup,
        menuCategory
      });
      const response = await fetch(`/api/v1/dashboard/salesforce/${storeId}/menu-items-monthly-trend?${params}`);
      if (!response.ok) {
        throw new Error('Failed to fetch menu items monthly trend');
      }
      return response.json();
    },
    enabled: enabled && !!storeId && !!menuGroup && !!menuCategory,
    
    staleTime: 10 * 60 * 1000,        
    gcTime: 24 * 60 * 60 * 1000,           
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};